import type { AvailableDaySlot } from '@brighterly/lib-core-types/options/interfaces'
import type { AxiosDefaultError } from '@brighterly/lib-core-types/options/types'
import type { AxiosResponse } from 'axios'
import type {
  AvailableBookingSlotsOptions,
  AvailableBookingSlotsPayload,
} from '@brighterly/lib-core-types/reschedule/interfaces'

import { useDisciplineStore } from '@entities/customer'
import { useToast } from '@shared/lib/toast'
import { useCsRoutes } from '@shared/api'
import { getAxiosErrorMessage } from '@shared/lib/http-errors'

import {
  getAvailableSlots,
  getAvailableDates,
  getCalendarDates,
} from '../../lib/reschedule/slots'
import { usePaidStore } from '../store'

export const useAvailableBookingSlotsForPaid = (payload: AvailableBookingSlotsPayload, options?: AvailableBookingSlotsOptions): void => {
  const { $axios } = useNuxtApp()

  const paidStore = usePaidStore()
  const disciplineStore = useDisciplineStore()
  const toast = useToast()

  if (!payload.booking.value) {
    toast.error('Unexpected error. Invalid booking')
    paidStore.setProcessing(false)
    paidStore.setModalReschedule(null)
    return
  } else {
    const url = useCsRoutes('booking.available-slots', {
      '[discipline_id]': disciplineStore.activeDiscipline!.id,
      '[booking_id]': payload.booking.value.id,
    })
    if (!url) {
      toast.error('Unexpected error. Invalid request URL')
      paidStore.setProcessing(false)
      paidStore.setModalReschedule(null)
    } else {
      options?.onStart?.()

      $axios.get(`${url}?month=${payload.month}`)
        .then((res: AxiosResponse<{ data: { days: AvailableDaySlot[] } }>) => {
          const availableSlots = getAvailableSlots(res.data.data.days)
          const availableDates = getAvailableDates(res.data.data.days)
          const calendarDates = getCalendarDates(payload.page)

          options?.onSuccess?.({
            availableSlots,
            availableDates,
            calendarDates,
          })
        })
        .catch((err: AxiosDefaultError) => {
          toast.error(getAxiosErrorMessage(err))

          options?.onError?.(err)

          paidStore.setProcessing(false)
          paidStore.setModalReschedule(null)
        })
        .finally(() => {
          options?.onFinish?.()
          paidStore.setProcessing(false)
        })
    }
  }
}
